import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Card, Table, Input, Select, Button, Space, Spin, Pagination, message, Badge, Typography } from 'antd';
import { RightOutlined, LeftOutlined, SearchOutlined } from '@ant-design/icons';
import { getDeviceGroupDevices, bindDeviceGroupDevice, unbindDeviceGroupDevice, getDevice, getProductList } from '@/api';

const { useMessage } = message;
const { Text } = Typography;

export interface BindDeviceModalProps {
  open: boolean;
  groupId: string | null;
  onCancel: () => void;
  onSuccess?: () => void;
}

// 共享的表格列定义，确保左右两侧完全一致
const getDeviceTableColumns = () => [
  {
    title: '设备名称',
    dataIndex: 'name',
    render: (text: string) => text || '-',
  },
  {
    title: '所属产品',
    dataIndex: 'product_name',
    render: (text: string) => text || '-',
  },
  {
    title: '状态',
    dataIndex: 'status',
    render(value: any, record: any) {
      return (
        <>
          <Badge
            color={
              (
                {
                  在线: 'green',
                  离线: 'gray',
                  禁用: 'red',
                  未激活: 'orange',
                } as any
              )[record.status] || 'gray'
            }
          />
          <Text className="ml5">{value || '未知'}</Text>
        </>
      );
    },
  },
];

const BindDeviceModal: React.FC<BindDeviceModalProps> = ({ open, groupId, onCancel, onSuccess }) => {
  const [messageApi, contextHolder] = useMessage();
  const [leftDevices, setLeftDevices] = useState<any[]>([]);
  const [rightDevices, setRightDevices] = useState<any[]>([]);
  const [leftSelectedKeys, setLeftSelectedKeys] = useState<string[]>([]);
  const [rightSelectedKeys, setRightSelectedKeys] = useState<string[]>([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [rightLoading, setRightLoading] = useState(false);
  const [productOptions, setProductOptions] = useState<any[]>([]);
  const [leftSearchName, setLeftSearchName] = useState<string>('');
  const [leftSearchProduct, setLeftSearchProduct] = useState<string>('');
  const [rightSearchName, setRightSearchName] = useState<string>('');
  const [rightSearchProduct, setRightSearchProduct] = useState<string>('');
  const [leftPagination, setLeftPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [rightPagination, setRightPagination] = useState({ page: 1, pageSize: 10, total: 0 });

  const deviceTableColumns = getDeviceTableColumns();

  // 加载产品列表
  const loadProductOptions = async () => {
    try {
      const response = await getProductList({ page: 1, pageSize: 1000 });
      if (response.success && response.result) {
        setProductOptions(response.result.list.map((item: any) => ({ label: item.name, value: item.id })));
      }
    } catch (error) {
      console.error('加载产品列表失败', error);
    }
  };

  // 加载左侧设备列表
  const loadLeftDevices = async (page = 1, pageSize = 10, excludeIds: string[] = []) => {
    try {
      setLeftLoading(true);
      const params: any = {
        page,
        pageSize,
      };
      if (leftSearchName) {
        params.name = leftSearchName;
      }
      if (leftSearchProduct) {
        params.product_id = leftSearchProduct;
      }
      // 添加 exclude_device_group_id 参数，排除当前分组下的设备
      if (groupId) {
        params.exclude_device_group_id = groupId;
      }

      const response = await getDevice(params);
      if (response.success && response.result) {
        let devices = response.result.list || [];
        // 过滤掉已经在右侧的设备
        const excludeDeviceIds = excludeIds.length > 0 ? excludeIds : rightDevices.map((item) => item.device_id || item.id);
        devices = devices.filter((item: any) => !excludeDeviceIds.includes(item.id));
        setLeftDevices(devices);
        setLeftPagination({
          page: response.result.page || page,
          pageSize: response.result.pageSize || pageSize,
          total: response.result.total || 0,
        });
      }
    } catch (error) {
      messageApi.error('加载设备列表失败');
    } finally {
      setLeftLoading(false);
    }
  };

  // 加载右侧设备列表（分组下的设备）
  const loadRightDevices = async (page = 1, pageSize = 10): Promise<any[] | null> => {
    if (!groupId) return null;
    try {
      setRightLoading(true);
      const response = await getDeviceGroupDevices({
        device_group_id: groupId,
        page,
        pageSize,
        name: rightSearchName || undefined,
        product_id: rightSearchProduct || undefined,
      });
      if (response.success && response.result) {
        // 确保数据字段与左侧表格一致，统一字段名
        const formattedDevices = (response.result.list || []).map((item: any) => ({
          ...item,
          // 确保 device_id 字段存在，作为右侧表格的主键
          device_id: item.device_id || item.deviceId || item.id || '',
          // 统一字段名，确保与左侧表格使用的字段一致
          name: item.name || item.device_name || item.deviceName || '',
          product_name: item.product_name || item.productName || item.product?.name || '',
          status: item.status || item.deviceStatus || '',
        }));
        // 去重：基于 device_id 去重，避免重复数据
        const uniqueDevices = formattedDevices.reduce((acc: any[], current: any) => {
          const existingIndex = acc.findIndex((item) => (item.device_id || item.id) === (current.device_id || current.id));
          if (existingIndex === -1) {
            acc.push(current);
          }
          return acc;
        }, []);
        setRightDevices(uniqueDevices);
        setRightPagination({
          page: response.result.page,
          pageSize: response.result.pageSize,
          total: response.result.total,
        });
        return uniqueDevices;
      }
      return null;
    } catch (error) {
      messageApi.error('加载已绑定设备失败');
      return null;
    } finally {
      setRightLoading(false);
    }
  };

  // 初始化标志，用于防止重复请求
  const isInitializingRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);
  // 手动操作标志，用于防止手动移动数据时触发自动重新加载
  const isManualMovingRef = React.useRef(false);

  // 当右侧设备列表更新时，重新加载左侧设备列表（排除初始化时和手动操作时）
  useEffect(() => {
    // 如果正在初始化、还未初始化或正在手动操作，不触发此 effect
    if (isInitializingRef.current || !hasInitializedRef.current || isManualMovingRef.current) return;
    if (open && groupId && rightDevices.length >= 0) {
      const rightIds = rightDevices.map((item) => item.device_id || item.id);
      loadLeftDevices(leftPagination.page, leftPagination.pageSize, rightIds);
    }
  }, [rightDevices.length, open, groupId]);

  // 当搜索条件改变时，重新加载左侧设备列表（重置到第一页）
  useEffect(() => {
    // 如果还未初始化，不触发此 effect
    if (!hasInitializedRef.current) return;
    if (open && groupId) {
      const rightIds = rightDevices.map((item) => item.device_id || item.id);
      const currentPageSize = leftPagination.pageSize || 10;
      setLeftPagination((prev) => ({ ...prev, page: 1 }));
      loadLeftDevices(1, currentPageSize, rightIds);
    }
  }, [leftSearchName, leftSearchProduct]);

  // 当右侧搜索条件改变时，重新加载右侧设备列表（重置到第一页）
  useEffect(() => {
    // 如果还未初始化，不触发此 effect
    if (!hasInitializedRef.current) return;
    if (open && groupId) {
      setRightPagination((prev) => ({ ...prev, page: 1 }));
      loadRightDevices(1, rightPagination.pageSize);
    }
  }, [rightSearchName, rightSearchProduct]);

  // 打开时初始化数据
  useEffect(() => {
    if (open && groupId) {
      isInitializingRef.current = true;
      hasInitializedRef.current = false;
      setLeftSelectedKeys([]);
      setRightSelectedKeys([]);
      setLeftSearchName('');
      setLeftSearchProduct('');
      setRightSearchName('');
      setRightSearchProduct('');
      setRightDevices([]);
      setLeftDevices([]);
      setLeftPagination({ page: 1, pageSize: 10, total: 0 });
      setRightPagination({ page: 1, pageSize: 10, total: 0 });
      loadProductOptions();
      // 先加载右侧设备，加载完成后再加载左侧设备
      loadRightDevices(1, 10).then((loadedRightDevices) => {
        if (loadedRightDevices) {
          const rightIds = loadedRightDevices.map((item: any) => item.device_id || item.id);
          loadLeftDevices(1, 10, rightIds).finally(() => {
            isInitializingRef.current = false;
            hasInitializedRef.current = true;
          });
        } else {
          isInitializingRef.current = false;
          hasInitializedRef.current = true;
        }
      });
    } else {
      // 模态框关闭时重置标志
      hasInitializedRef.current = false;
      isInitializingRef.current = false;
    }
  }, [open, groupId]);

  // 向右转移（绑定设备）
  const handleMoveToRight = () => {
    isManualMovingRef.current = true;
    const selectedDevices = leftDevices.filter((item) => leftSelectedKeys.includes(item.id));
    // 确保设备数据格式一致，包含所有需要的字段，特别是 device_id 字段
    const formattedDevices = selectedDevices.map((device) => ({
      ...device,
      // 确保 device_id 字段存在，使用 id 作为 device_id（因为左侧设备使用 id）
      device_id: device.id || device.device_id || '',
      name: device.name || '',
      product_name: device.product_name || '',
      status: device.status || '',
    }));
    // 去重：过滤掉已经在右侧的设备，避免重复添加
    const existingDeviceIds = rightDevices.map((item) => item.device_id || item.id);
    const newDevices = formattedDevices.filter((device) => !existingDeviceIds.includes(device.device_id || device.id));
    setRightDevices([...rightDevices, ...newDevices]);
    setLeftDevices(leftDevices.filter((item) => !leftSelectedKeys.includes(item.id)));
    setLeftSelectedKeys([]);
    // 延迟重置标志，确保 useEffect 不会触发
    setTimeout(() => {
      isManualMovingRef.current = false;
    }, 100);
  };

  // 向左转移（解绑设备）
  const handleMoveToLeft = () => {
    isManualMovingRef.current = true;
    const unboundDevices = rightDevices.filter((item) => rightSelectedKeys.includes(item.device_id || item.id));
    // 使用函数式更新，确保使用最新状态
    setRightDevices((prev) => prev.filter((item) => !rightSelectedKeys.includes(item.device_id || item.id)));
    setLeftDevices((prev) => {
      // 合并数据，避免重复
      const existingIds = new Set(prev.map((item) => item.id));
      const newDevices = unboundDevices.filter((device) => {
        const deviceId = device.device_id || device.id;
        return deviceId && !existingIds.has(deviceId);
      });
      return [...prev, ...newDevices];
    });
    setRightSelectedKeys([]);
    // 延迟重置标志，确保 useEffect 不会触发
    setTimeout(() => {
      isManualMovingRef.current = false;
    }, 100);
  };

  // 确定绑定/解绑
  const handleConfirm = async () => {
    if (!groupId) return;

    const currentRightIds = rightDevices.map((item) => item.device_id || item.id);

    try {
      let allOriginalDevices: any[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const originalResponse = await getDeviceGroupDevices({
          device_group_id: groupId,
          page,
          pageSize,
        });

        if (originalResponse.success && originalResponse.result?.list) {
          allOriginalDevices = [...allOriginalDevices, ...originalResponse.result.list];
          if (originalResponse.result.list.length < pageSize) {
            break;
          }
          page++;
        } else {
          break;
        }
      }

      // 统一使用 device_id 或 id 进行比较
      const originalIds = allOriginalDevices.map((item: any) => item.device_id || item.id || '').filter(Boolean);
      const currentRightIdsSet = new Set(currentRightIds.filter(Boolean));

      // 计算需要绑定的设备（在右侧但不在原始列表中的）
      const toBindIds = currentRightIds.filter((id) => id && !originalIds.includes(id));
      // 计算需要解绑的设备（在原始列表中但不在右侧的）
      const toUnbindIds = originalIds.filter((id: string) => id && !currentRightIdsSet.has(id));

      if (toBindIds.length > 0) {
        const bindResponse = await bindDeviceGroupDevice({
          device_group_id: groupId,
          device_ids: toBindIds,
        });
        if (!bindResponse.success) {
          messageApi.error(bindResponse.errorMsg || '绑定设备失败');
          return;
        }
      }

      if (toUnbindIds.length > 0) {
        const unbindResponse = await unbindDeviceGroupDevice({
          device_group_id: groupId,
          device_ids: toUnbindIds,
        });
        if (!unbindResponse.success) {
          messageApi.error(unbindResponse.errorMsg || '解绑设备失败');
          return;
        }
      }

      if (toBindIds.length === 0 && toUnbindIds.length === 0) {
        messageApi.info('没有需要更新的设备');
      } else {
        messageApi.success('操作成功');
      }
      onCancel();
      onSuccess?.();
    } catch (error) {
      messageApi.error('操作失败');
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        title="设备绑定"
        open={open}
        onCancel={onCancel}
        width={1200}
        footer={[
          <Button key="cancel" onClick={onCancel}>
            取消
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirm}>
            确定
          </Button>,
        ]}
      >
        <style>
          {`
            .bind-device-modal .ant-space-item:last-child {
              flex: 1;
            }
            .bind-device-modal .ant-space-item:last-child .ant-input {
              width: 100%;
            }
          `}
        </style>
        <Row gutter={16}>
          <Col span={11}>
            <Card title={`设备列表-${leftSelectedKeys.length || 0}项`} size="small" styles={{ body: { padding: '10px', height: '500px' } }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space className="bind-device-modal" style={{ width: '100%', display: 'flex' }}>
                    <Select placeholder="设备名称" style={{ width: 120 }} options={[{ label: '设备名称', value: 'name' }]} defaultValue="name" disabled />
                    <Input
                      placeholder="关键词搜索"
                      value={leftSearchName}
                      onChange={(e) => setLeftSearchName(e.target.value)}
                      allowClear
                      suffix={
                        <SearchOutlined
                          onClick={() => {
                            setLeftPagination((prev) => ({ ...prev, page: 1 }));
                            loadLeftDevices(1, leftPagination.pageSize);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      }
                      onPressEnter={() => {
                        setLeftPagination((prev) => ({ ...prev, page: 1 }));
                        loadLeftDevices(1, leftPagination.pageSize);
                      }}
                    />
                  </Space>
                  <Select
                    placeholder="所属产品 (全部)"
                    value={leftSearchProduct}
                    onChange={(value) => {
                      setLeftSearchProduct(value);
                      setLeftPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    style={{ width: '100%' }}
                    options={[{ label: '全部产品', value: '' }, ...productOptions]}
                  />
                </Space>
                <Spin spinning={leftLoading} style={{ display: 'flex', flexDirection: 'column' }}>
                  <Table
                    style={{ minHeight: '350px' }}
                    rowKey="id"
                    size="small"
                    // scroll={{ y: 350 }}
                    dataSource={leftDevices}
                    rowSelection={{
                      selectedRowKeys: leftSelectedKeys,
                      onChange: (keys) => setLeftSelectedKeys(keys as string[]),
                    }}
                    columns={deviceTableColumns}
                    pagination={false}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <Pagination
                      current={leftPagination.page}
                      pageSize={leftPagination.pageSize}
                      total={leftPagination.total}
                      size="small"
                      showTotal={(total) => `共${total}项`}
                      showQuickJumper
                      onChange={(page, pageSize) => {
                        setLeftPagination((prev) => ({ ...prev, page, pageSize }));
                        loadLeftDevices(page, pageSize);
                      }}
                    />
                  </div>
                </Spin>
              </Space>
            </Card>
          </Col>
          <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Space direction="vertical" size="middle">
              <Button type="primary" icon={<RightOutlined />} disabled={!leftSelectedKeys.length} onClick={handleMoveToRight} />
              <Button type="primary" icon={<LeftOutlined />} disabled={!rightSelectedKeys.length} onClick={handleMoveToLeft} />
            </Space>
          </Col>
          <Col span={11}>
            <Card title={`已绑定设备-${rightSelectedKeys.length ? `${rightSelectedKeys.length}/` : ''}${rightDevices.length}项`} size="small" styles={{ body: { padding: '10px', height: '500px' } }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Space className="bind-device-modal" style={{ width: '100%', display: 'flex' }}>
                    <Select placeholder="设备名称" style={{ width: 120 }} options={[{ label: '设备名称', value: 'name' }]} defaultValue="name" disabled />
                    <Input
                      placeholder="关键词搜索"
                      value={rightSearchName}
                      onChange={(e) => {
                        setRightSearchName(e.target.value);
                      }}
                      allowClear
                      suffix={
                        <SearchOutlined
                          onClick={() => {
                            loadRightDevices(1, rightPagination.pageSize);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      }
                      onPressEnter={() => {
                        loadRightDevices(1, rightPagination.pageSize);
                      }}
                    />
                  </Space>
                  <Select
                    placeholder="所属产品 (全部)"
                    value={rightSearchProduct}
                    onChange={(value) => {
                      setRightSearchProduct(value);
                      setRightPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                    style={{ width: '100%' }}
                    options={[{ label: '全部产品', value: '' }, ...productOptions]}
                  />
                </Space>
                <Spin spinning={rightLoading}>
                  <Table
                    rowKey={(record) => record?.device_id || record?.id || ''}
                    size="small"
                    // scroll={{ y: 350 }}
                    style={{ minHeight: '350px' }}
                    dataSource={rightDevices}
                    rowSelection={{
                      selectedRowKeys: rightSelectedKeys,
                      onChange: (keys) => {
                        // 确保使用正确的 device_id 作为主键
                        const validKeys = (keys as React.Key[]).map((key) => String(key)).filter((key) => key);
                        setRightSelectedKeys(validKeys);
                      },
                    }}
                    columns={deviceTableColumns}
                    pagination={false}
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <Pagination
                      current={rightPagination.page}
                      pageSize={rightPagination.pageSize}
                      total={rightPagination.total}
                      size="small"
                      showTotal={(total) => `共${total}项`}
                      onChange={(page, pageSize) => {
                        loadRightDevices(page, pageSize);
                      }}
                    />
                  </div>
                </Spin>
              </Space>
            </Card>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default BindDeviceModal;
